import React, { useState, useRef, useEffect } from 'react';
import { HydratedPost, UserData } from '../types';
import {
    TrashIcon,
    EditIcon,
    BookmarkIcon,
    BumpIcon,
    CheckmarkIcon,
    SendIcon,
    CommentIcon,
    LikeIcon,
    MoreHorizontalIcon,
    VolumeOffIcon,
    BrokenHeartIcon,
    PrivateIcon,
    FlagIcon
} from './Icons';

interface PostActionButtonBarProps {
    post: HydratedPost;
    currentUser: string | null;
    usersData: Record<string, UserData>;
    onDelete: (postId: number, post?: HydratedPost) => void;
    onEdit: (post: HydratedPost) => void;
    onBookmark: (postId: number) => void;
    onBump: (post: HydratedPost) => void;
    onVouch?: (postId: number) => void;
    onShare?: (post: HydratedPost, slideIndex?: number) => void;
    onLike: (postId: number) => void;
    onMuteUser?: (userId: string) => void;
    onBlockUser?: (userId: string) => void;
    onGrantAdmin?: (userId: string) => void;
    onDeleteUser?: (userId: string) => void;
    handlePostClickWithMediaStop: (e: React.MouseEvent) => void;
    animatingLikes: Set<number>;
    animatingBumps: Set<number>;
    animatingBookmarks: Set<number>;
    onReportPost?: (postId: number) => void;
    onArchive?: (postId: number) => void;
    onUnarchive?: (postId: number) => void;
    isDemo?: boolean;
    isSimpleBump?: boolean;
    bumperAuthor?: string;
    onPin?: (postId: number) => void;
    onUnpin?: (postId: number) => void;
    currentImageIndex?: number;
    onToggleFit?: (postId: number, mediaIndex: number) => void;
    onToggleShape?: (postId: number) => void;
    isCardHovered?: boolean;
    onOptionsOpenChange?: (isOpen: boolean) => void;
}

export const PostActionButtonBar = ({
    post,
    currentUser,
    usersData,
    onDelete,
    onEdit,
    onBookmark,
    onBump,
    onVouch,
    onShare,
    onLike,
    onMuteUser,
    onBlockUser,
    onGrantAdmin,
    onDeleteUser,
    handlePostClickWithMediaStop,
    animatingLikes,
    animatingBumps,
    animatingBookmarks,
    onReportPost,
    onArchive,
    onUnarchive,
    isDemo = false,
    isSimpleBump = false,
    bumperAuthor,
    onPin,
    onUnpin,
    currentImageIndex = 0,
    onToggleFit,
    onToggleShape,
    isCardHovered = false,
    onOptionsOpenChange
}: PostActionButtonBarProps) => {
    const [isOptionsOpen, setIsOptionsOpen] = useState(false);
    const [isForcedHidden, setIsForcedHidden] = useState(false);

    useEffect(() => {
        onOptionsOpenChange?.(isOptionsOpen);
    }, [isOptionsOpen, onOptionsOpenChange]);
    const optionsMenuRef = useRef<HTMLDivElement>(null);

    const hasRefittableMedia = post.type === 'image' || post.type === 'video' || ((post.type === 'audio' || post.type === 'voice') && !!post.content.coverImageUrl);

    // Reset forced hidden state when the card is no longer hovered
    useEffect(() => {
        if (!isCardHovered) {
            setIsForcedHidden(false);
        }
    }, [isCardHovered]);

    // Close options menu and hide the action bar when clicking outside
    useEffect(() => {
        if (!isOptionsOpen) return;
        const handleOutsideClick = (e: MouseEvent) => {
            if (optionsMenuRef.current && !optionsMenuRef.current.contains(e.target as Node)) {
                setIsOptionsOpen(false);
                setIsForcedHidden(true);
            }
        };
        window.addEventListener('mousedown', handleOutsideClick, true);
        return () => window.removeEventListener('mousedown', handleOutsideClick, true);
    }, [isOptionsOpen]);

    return (
        <div className={`absolute top-0 right-0 p-2 transition-opacity duration-300 z-30 ${isForcedHidden ? 'pointer-events-none opacity-0' : (isOptionsOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 group-hover:opacity-100')} ${isDemo ? 'grayscale-[0.5] opacity-60' : ''}`} aria-hidden="true">
            <div className="flex items-center gap-1 text-white bg-black/50 backdrop-blur-sm rounded-full p-1 shadow-lg">
                {((post.author === currentUser && !isSimpleBump) || (isSimpleBump && bumperAuthor === currentUser)) && (
                    <>
                        {!isSimpleBump && (
                            <button
                                className="p-2 rounded-full transition-all duration-150 transform hover:scale-110 active:scale-95 hover:bg-white/10"
                                onClick={(e) => { e.stopPropagation(); onEdit(post); }}
                                aria-label="Edit Post"
                            >
                                <EditIcon className={`${post.author === currentUser ? 'w-3 h-3' : 'w-5 h-5'}`} />
                            </button>
                        )}
                    </>
                )}

                {post.author === currentUser && post.type !== 'link' && post.type !== 'text' && (
                    <button
                        className="p-2 rounded-full transition-all duration-150 transform hover:scale-110 active:scale-95 hover:bg-white/10 flex items-center justify-center min-w-[28px]"
                        onClick={(e) => { e.stopPropagation(); onToggleShape?.(post.id); }}
                        aria-label="Toggle Shape"
                        title="Toggle Postcard Shape"
                    >
                        <span className="text-[8px] font-black uppercase tracking-tighter leading-none px-1">
                            {post.postcardShape === 'square' ? '1:1' : (post.postcardShape === 'tall' ? 'tall' : 'wide')}
                        </span>
                    </button>
                )}

                {post.author === currentUser ? (
                    hasRefittableMedia && (
                        <button
                            className="p-2 rounded-full transition-all duration-150 transform hover:scale-110 active:scale-95 hover:bg-white/10 flex items-center justify-center"
                            onClick={(e) => { e.stopPropagation(); onToggleFit && onToggleFit(post.id, currentImageIndex); }}
                            aria-label="Refit Media"
                            title="Refit Media"
                        >
                            {(() => {
                                const isAudioVoice = post.type === 'audio' || post.type === 'voice';
                                const currentFit = isAudioVoice ? (post.content.coverImageFit || 'auto') : (post.content.media?.[currentImageIndex]?.fit || 'auto');
                                const iconClass = post.author === currentUser ? 'w-3.5 h-3.5' : 'w-5 h-5';
                                if (currentFit === 'horizontal') {
                                    return (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={iconClass}>
                                            <path d="M3 12h18M7 8l-4 4 4 4M17 8l4 4-4 4" />
                                        </svg>
                                    );
                                } else if (currentFit === 'vertical') {
                                    return (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={iconClass}>
                                            <path d="M12 3v18M8 7l4-4 4 4M8 17l4 4 4-4" />
                                        </svg>
                                    );
                                } else {
                                    return (
                                        <span className="text-[8px] font-black uppercase tracking-tighter leading-none">auto</span>
                                    );
                                }
                            })()}
                        </button>
                    )
                ) : null}

                {!post.linkRequestDetails?.isLinkRequest && post.author !== currentUser && (
                    <button
                        className="p-2 rounded-full transition-all duration-150 transform hover:scale-110 active:scale-95 hover:bg-white/10"
                        onClick={(e) => { e.stopPropagation(); onBookmark(post.id); }}
                        aria-label="Bookmark"
                    >
                        <BookmarkIcon isBookmarked={post.isBookmarked} className={`${post.author === currentUser ? 'w-3 h-3' : 'w-5 h-5'} ${animatingBookmarks.has(post.id) ? 'animate-bookmark-pop' : ''}`} />
                    </button>
                )}

                {!post.linkRequestDetails?.isLinkRequest && (
                    <button
                        className="p-2 rounded-full transition-all duration-150 transform hover:scale-110 active:scale-95 hover:bg-white/10"
                        onClick={(e) => { e.stopPropagation(); onBump(post); }}
                        aria-label="Bump"
                    >
                        <BumpIcon isBumped={post.isBumped} className={`${post.author === currentUser ? 'w-3 h-3' : 'w-5 h-5'} ${animatingBumps.has(post.id) ? 'animate-bump-spin' : ''}`} />
                    </button>
                )}

                {!post.linkRequestDetails?.isLinkRequest && (
                    <button className="p-2 rounded-full transition-all duration-150 transform hover:scale-110 active:scale-95 hover:bg-white/10"
                        onClick={(e) => { e.stopPropagation(); if (onShare) onShare(post); }}
                        aria-label="Share"
                    >
                        <SendIcon className={`${post.author === currentUser ? 'w-3 h-3' : 'w-5 h-5'}`} />
                    </button>
                )}

                <button
                    className="p-2 rounded-full transition-all duration-150 transform hover:scale-110 active:scale-95 hover:bg-white/10"
                    onClick={(e) => { e.stopPropagation(); handlePostClickWithMediaStop(e); }}
                    aria-label="Comment"
                >
                    <CommentIcon className={`${post.author === currentUser ? 'w-3 h-3' : 'w-5 h-5'}`} />
                </button>

                {post.footprintDetails ? (
                    <button
                        className="p-2 rounded-full transition-all duration-150 transform hover:scale-110 active:scale-95 hover:bg-white/10"
                        onClick={(e) => { e.stopPropagation(); onVouch && onVouch(post.id); }}
                        aria-label="Vouch"
                    >
                        <CheckmarkIcon isVouched={post.vouches?.some(v => v.userId === currentUser)} className={`${post.author === currentUser ? 'w-3 h-3' : 'w-5 h-5'}`} />
                    </button>
                ) : (
                    <button
                        className="p-2 rounded-full transition-all duration-150 transform hover:scale-110 active:scale-95 hover:bg-white/10"
                        onClick={(e) => { e.stopPropagation(); onLike(post.id); }}
                        aria-label="Like"
                    >
                        <LikeIcon isLiked={post.isLiked} className={`${post.author === currentUser ? 'w-3 h-3' : 'w-5 h-5'}  ${animatingLikes.has(post.id) ? 'animate-heart-burst' : ''}`} />
                    </button>
                )}

                <div className="relative" ref={optionsMenuRef}>
                    <button
                        className="p-2 rounded-full transition-all duration-150 transform hover:scale-110 active:scale-95 hover:bg-white/10"
                        onClick={(e) => { e.stopPropagation(); setIsOptionsOpen(!isOptionsOpen); }}
                        aria-label="More Options"
                    >
                        <MoreHorizontalIcon className={`${post.author === currentUser ? 'w-3 h-3' : 'w-5 h-5'}`} />
                    </button>

                    {isOptionsOpen && (
                        <div className="absolute top-full right-0 mt-2 w-48 bg-gray-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-700/50 py-1.5 z-50 animate-fade-in-fast overflow-hidden">
                            {post.author !== currentUser && (
                                <>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onReportPost && onReportPost(post.id); setIsOptionsOpen(false); }}
                                        className="w-full text-left px-4 py-2.5 hover:bg-yellow-500/10 text-yellow-500 transition-colors flex items-center gap-3"
                                    >
                                        <FlagIcon className="w-4 h-4 text-yellow-500" />
                                        <span className="text-sm font-medium">Report Content</span>
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onMuteUser && onMuteUser(post.author); setIsOptionsOpen(false); }}
                                        className="w-full text-left px-4 py-2.5 hover:bg-white/10 transition-colors flex items-center gap-3"
                                    >
                                        <VolumeOffIcon className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm font-medium text-white">
                                            {usersData[currentUser || '']?.mutedUsers?.includes(post.author) ? 'Unmute' : 'Mute'} @{post.author}
                                        </span>
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onBlockUser && onBlockUser(post.author); setIsOptionsOpen(false); }}
                                        className="w-full text-left px-4 py-2.5 hover:bg-red-500/10 text-red-400 transition-colors flex items-center gap-3"
                                    >
                                        <BrokenHeartIcon className="w-4 h-4 text-red-500" />
                                        <span className="text-sm font-medium">
                                            {usersData[currentUser || '']?.blockedUsers?.includes(post.author) ? 'Unblock' : 'Block'} @{post.author}
                                        </span>
                                    </button>
                                    {currentUser && usersData[currentUser]?.role === 'admin' && onGrantAdmin && post.author.toLowerCase() !== 'agk' && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onGrantAdmin(post.author); setIsOptionsOpen(false); }}
                                            className="w-full text-left px-4 py-2.5 hover:bg-white/10 transition-colors flex items-center gap-3 border-t border-gray-700/50 mt-1"
                                        >
                                            <PrivateIcon className="w-4 h-4 text-yellow-500" />
                                            <span className="text-sm font-medium text-white">
                                                {usersData[post.author]?.role === 'admin' ? 'Revoke Admin' : 'Grant Admin'}
                                            </span>
                                        </button>
                                    )}
                                    {currentUser && usersData[currentUser]?.role === 'admin' && onDeleteUser && post.author.toLowerCase() !== 'agk' && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsOptionsOpen(false);
                                                const confirmName = prompt(`⚠️ DANGER: Verify deletion.\n\nType "${post.author}" to confirm permanent deletion of this user and ALL their data.`);
                                                if (confirmName === post.author) {
                                                    onDeleteUser(post.author);
                                                } else if (confirmName !== null) {
                                                    alert("Username mismatch. Deletion cancelled.");
                                                }
                                            }}
                                            className="w-full text-left px-4 py-2.5 hover:bg-red-500/10 text-red-400 transition-colors flex items-center gap-3"
                                        >
                                            <TrashIcon className="w-4 h-4 text-red-500" />
                                            <span className="text-sm font-medium">Delete User</span>
                                        </button>
                                    )}
                                </>
                            )}
                            {post.author === currentUser && (
                                <div className="px-4 py-2 text-xs text-gray-500 font-semibold uppercase tracking-wider">Posts Options</div>
                            )}

                            {post.author === currentUser && (
                                <>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onBookmark(post.id);
                                            setIsOptionsOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-2.5 hover:bg-white/10 text-white transition-colors flex items-center gap-3 border-t border-gray-700/50"
                                    >
                                        <BookmarkIcon isBookmarked={post.isBookmarked} className="w-4 h-4 text-white" />
                                        <span className="text-sm font-medium">{post.isBookmarked ? 'Unbookmark Post' : 'Bookmark Post'}</span>
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const isPinned = usersData[currentUser || '']?.pinnedPosts?.includes(post.id);
                                            if (isPinned) onUnpin?.(post.id);
                                            else onPin?.(post.id);
                                            setIsOptionsOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-2.5 hover:bg-blue-500/10 text-blue-400 transition-colors flex items-center gap-3 border-t border-gray-700/50"
                                    >
                                        <span className="text-sm font-medium">
                                            {usersData[currentUser || '']?.pinnedPosts?.includes(post.id) ? '📌 Unpin Post' : '📍 Pin Post'}
                                        </span>
                                    </button>
                                    <button
                                        onClick={(e) => { 
                                            e.stopPropagation(); 
                                            if (post.isArchived) onUnarchive?.(post.id);
                                            else onArchive?.(post.id);
                                            setIsOptionsOpen(false); 
                                        }}
                                        className="w-full text-left px-4 py-2.5 hover:bg-yellow-500/10 text-yellow-400 transition-colors flex items-center gap-3 border-t border-gray-700/50"
                                    >
                                        <BookmarkIcon className={`w-4 h-4 ${post.isArchived ? 'text-blue-400' : 'text-yellow-500'}`} />
                                        <span className="text-sm font-medium">{post.isArchived ? 'Unarchive Post' : 'Archive Post'}</span>
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDelete(post.id, post);
                                            setIsOptionsOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-2.5 hover:bg-red-500/10 text-red-400 transition-colors flex items-center gap-3 border-t border-gray-700/50"
                                    >
                                        <TrashIcon className="w-4 h-4 text-red-500" />
                                        <span className="text-sm font-medium">{isSimpleBump ? 'Remove Bump' : 'Delete Post'}</span>
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
